import EnabledCheckIcon from '@/components/Icons/EnabledCheckIcon';
import UploadIcon from '@/components/Icons/UploadIcon';
import { Cancel, Close } from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import classNames from 'classnames';
import { memo, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

interface ITagFileUploadProps {
  loading: boolean;
  disabled?: boolean;
  setUploadedFiles: (files: File[]) => void;
}

const baseDndClassname = 'flex justify-center items-center flex-col w-full h-[214px] border rounded-lg';

const TagFileUpload = memo(({ loading, setUploadedFiles, disabled }: ITagFileUploadProps) => {
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [hasUploadFailed, setHasUploadFailed] = useState(false);

  const classNameArgs = {
    'cursor-pointer': !disabled,
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setHasUploadFailed(false);

      if (!acceptedFiles.length) {
        toast.info('Please select an XLS or XLSX file');
        return;
      }

      const file = acceptedFiles[0];

      if (file.size > MAX_FILE_SIZE) {
        toast.error(`"${file.name}" exceeds the 50 MB file limit`);
        setHasUploadFailed(true);
        return;
      }

      setAttachedFile(file);
      setUploadedFiles([file]);
    },
    [setUploadedFiles],
  );

  const removeFile = useCallback(() => {
    setAttachedFile(null);
    setUploadedFiles([]);
  }, [setUploadedFiles]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    multiple: false,
  });

  if (hasUploadFailed) {
    return (
      <Box
        {...getRootProps()}
        className={classNames(baseDndClassname, 'bg-[#FF3B30] bg-opacity-[.2] border-[#ADACB0]', classNameArgs)}
      >
        <Cancel color='error' sx={{ width: 40, height: 40 }} />
        <span className='text-center text-lg font-semibold justify-between mt-2'>File Upload Failed</span>
        <span className='text-center text-sm font-semibold justify-between mt-3 cursor-pointer'>
          Choose file to retry upload
        </span>
        <input {...getInputProps()} />
      </Box>
    );
  }

  if (attachedFile) {
    return (
      <Box
        {...getRootProps()}
        className={classNames(baseDndClassname, 'bg-[#2C6E49] bg-opacity-[.15] pr-3 border-[#1b4332]', classNameArgs)}
      >
        <input {...getInputProps()} />
        <EnabledCheckIcon width={40} height={40} />
        <span className='text-center text-lg font-semibold justify-between mt-2'>File Attached</span>
        <div className='flex items-center justify-center gap-x-1 mt-2 px-3'>
          <span className='text-[#1b4332] text-sm font-medium truncate'>{attachedFile.name}</span>
          {!loading && (
            <IconButton
              size='small'
              onClick={e => {
                e.stopPropagation();
                removeFile();
              }}
              sx={{ p: 0.25, border: '1px solid #000', borderRadius: '50%' }}
            >
              <Close sx={{ fontSize: 12, color: '#000' }} />
            </IconButton>
          )}
        </div>
        {loading && <span className='text-sm font-semibold mt-3'>Uploading...</span>}
      </Box>
    );
  }

  return (
    <Box
      {...getRootProps()}
      className={classNames(baseDndClassname, 'bg-[#F8F8F8] pr-3 border-dashed border-[#ADACB0]', classNameArgs)}
    >
      <input disabled={disabled} {...getInputProps()} />
      <div className='bg-[#E6E6E7] rounded-lg p-2.5'>
        <UploadIcon />
      </div>
      <span className='mt-2 text-sm font-medium'>Drag & drop files or browse files</span>
    </Box>
  );
});

export default TagFileUpload;
